/**
 * Coming Soon "Notify me" modal.
 *
 * On submit, records a "Coming Soon Signup" event on the shopper's Klaviyo
 * profile through the client Events API (public company id, meant for
 * browsers). Klaviyo creates the profile when the email is new. The same
 * product details are written as profile properties, so a segment can be
 * built on either the event or the properties. No list subscription.
 *
 * The modal itself (open, close, focus, scroll lock) is the theme's
 * popup-component; this element only handles the form inside it and keeps
 * the hidden variant fields in sync with `theme:variant:change`.
 */
(function () {
  'use strict';

  if (customElements.get('coming-soon-form')) return;

  const KLAVIYO_REVISION = '2026-07-15';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PROPERTY_KEYS = [
    'product_id',
    'product_handle',
    'product_title',
    'product_url',
    'product_image',
    'variant_id',
    'variant_title',
    'sku',
    'price',
    'currency',
    'release_date',
    'campaign_name',
    'store',
    'locale',
  ];

  class ComingSoonForm extends HTMLElement {
    constructor() {
      super();
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('[data-coming-soon-form]');
      if (!this.form) return;

      this.message = this.form.querySelector('[data-message]');
      this.submitButton = this.form.querySelector('button[type="submit"]');
      this.companyId = this.dataset.companyId || '';
      this.metric = this.dataset.metric || 'Coming Soon Signup';
      // Only react to variant changes from this product, not from upsells on the same page.
      this.scope = this.closest('product-component') || this.closest('[data-product]');

      document.addEventListener('theme:variant:change', this.onVariantChange);
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:variant:change', this.onVariantChange);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      if (this.scope && event.target && !this.scope.contains(event.target)) return;

      this.setField('[data-variant-id]', variant.id);
      this.setField('[data-variant-title]', variant.title || '');
      this.setField('[data-variant-sku]', variant.sku || '');
      if (typeof variant.price === 'number') {
        this.setField('[data-variant-price]', (variant.price / 100).toFixed(2));
      }
    }

    setField(selector, value) {
      const input = this.form.querySelector(selector);
      if (input) input.value = value == null ? '' : String(value);
    }

    async onSubmit(event) {
      event.preventDefault();
      const data = new FormData(this.form);

      // Honeypot filled: pretend it worked, never call Klaviyo.
      if (data.get('website')) {
        this.showSuccess();
        return;
      }

      const email = String(data.get('email') || '').trim();
      if (!EMAIL_RE.test(email)) {
        this.showMessage(this.dataset.msgInvalid, 'error');
        return;
      }

      const props = this.collectProperties(data);

      this.setBusy(true);
      try {
        await this.track(email, props);
        this.showSuccess();
      } catch (error) {
        if (window.console) console.error('[coming-soon] signup failed', error);
        this.showMessage(this.dataset.msgError, 'error');
      } finally {
        this.setBusy(false);
      }
    }

    collectProperties(data) {
      const props = {};
      for (const key of PROPERTY_KEYS) {
        const value = data.get(key);
        if (value != null && value !== '') props[key] = String(value);
      }
      return props;
    }

    /**
     * One call: the event on the profile, plus profile properties with the
     * latest product asked about. Creates the profile if the email is new.
     */
    track(email, props) {
      // Same shopper, same variant, same day: Klaviyo keeps only the first
      // event, so a double submit never creates a duplicate.
      const day = new Date().toISOString().slice(0, 10);
      const uniqueId = ['coming-soon', email.toLowerCase(), props.product_id || '', props.variant_id || '', day].join('|');

      return this.post('events', {
        data: {
          type: 'event',
          attributes: {
            unique_id: uniqueId,
            properties: props,
            metric: { data: { type: 'metric', attributes: { name: this.metric } } },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: {
                    coming_soon_signup: true,
                    coming_soon_last_signup_at: new Date().toISOString(),
                    coming_soon_last_product_id: props.product_id || '',
                    coming_soon_last_product: props.product_title || '',
                    coming_soon_last_product_url: props.product_url || '',
                    coming_soon_last_variant: props.variant_title || '',
                    coming_soon_last_sku: props.sku || '',
                    coming_soon_last_release_date: props.release_date || '',
                    coming_soon_last_campaign: props.campaign_name || '',
                    coming_soon_last_store: props.store || '',
                  },
                },
              },
            },
          },
        },
      });
    }

    async post(path, body) {
      const url = `https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(this.companyId)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        throw new Error(`Klaviyo ${path} responded ${response.status}`);
      }
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      if (this.submitButton) this.submitButton.disabled = busy;
    }

    showSuccess() {
      this.form.reset();
      this.classList.add('is-success');
      this.showMessage(this.dataset.msgSuccess, 'success');
      const closeButton = this.closest('dialog')?.querySelector('[data-popup-close]');
      if (closeButton) closeButton.focus();
    }

    showMessage(text, type) {
      if (!this.message) return;
      this.message.textContent = text || '';
      this.message.classList.remove('coming-soon-form__message--error', 'coming-soon-form__message--success');
      this.message.classList.add(`coming-soon-form__message--${type}`);
      this.message.hidden = !text;
    }
  }

  customElements.define('coming-soon-form', ComingSoonForm);
})();
